import {useCallback, useMemo} from 'react'
import {Box, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {selectActiveScene, setDeviceRotationZ} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import AnglePicker from '../sharedWidgets/AnglePicker'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── DeviceRotationPanel — Z-axis rotation control for the device ───

const DeviceRotationPanel = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const deviceRotationZ = useAppSelector((s) => selectActiveScene(s).deviceRotationZ)

  const handleChange = useCallback((angle: number) => dispatch(setDeviceRotationZ(angle)), [dispatch])
  const handleDragEnd = useCallback(() => dispatch(sealUndo()), [dispatch])

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Box sx={sx.root}>
      <Typography sx={sx.label}>{t('device.rotation.title')}</Typography>
      <Box sx={sx.row}>
        <AnglePicker value={deviceRotationZ} onChange={handleChange} onDragEnd={handleDragEnd} />
        <Typography sx={sx.value}>{deviceRotationZ}°</Typography>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    marginTop: '12px',
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: tokens.text.muted,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  value: {
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text.primary,
    minWidth: 40,
  },
})

export default DeviceRotationPanel
